import { ArrowCcwIcon, CouponIcon, PenIcon, TicketIcon, TrashIcon } from "@xola/icons";
import { Alert, Badge, Dot, flash, getSymbol, Number, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { isEmpty, range, sortBy } from "lodash";
import React, { ReactNode, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import {
    BxgyCoupon,
    Coupon,
    CouponScope,
    CouponType,
    CouponTypeCopy,
    LineItemType,
    Permission,
    PurchaseRuleShortcutBXGYOption,
    PurchaseRuleShortcutDiscountOption,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
    Source,
    VoucherCoupon,
} from "types";
import { Currency } from "../Common/Currency";
import { NoTranslate } from "../Common/NoTranslate";
import { PromptModal } from "../Common/PromptModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { SkeletonLine } from "../Common/Skeleton/TableSkeleton";
import { ErrorBoundary } from "../ErrorBoundary";
import { Loader } from "../Loader";
import { api } from "@/api/api";
import { NoResults } from "@/components/Common/NoResults";
import { useCatalogItems } from "@/hooks/resources/useCatalogItems";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { CreateCouponButton } from "@/pages/Marketing/CouponPage";

interface CouponItemProps {
    coupon?: Coupon;
    isCouponEditable?: boolean;
    className?: string;
    demographicsData?: (DemographicsData | undefined)[];
}

interface DemographicsData {
    id: string;
    name: string;
}

interface CouponSummaryProps {
    coupon: Coupon;
    demographicData?: (DemographicsData | undefined)[];
}

export const CouponItem = ({ coupon, isCouponEditable = false, className, demographicsData }: CouponItemProps) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const { mutateAsync: deleteCoupon, isLoading } = useMutation(async (id: ID) => {
        return api.delete(`/coupons/${id}`);
    });

    const handleClick = () => {
        history.push(`/marketing/coupons/${coupon?.id}/description`);
    };

    const handleDelete = async (e: any) => {
        e.stopPropagation();
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (coupon?.id) {
            await deleteCoupon(coupon.id);
            invalidate("coupons", coupon.id);
            invalidate("codes");
            invalidate("couponCodes");
            flash.success("Coupon deleted");
        }

        setIsDeleteModalOpen(false);
    };

    return coupon ? (
        <div
            className={clsx(
                "ui-coupon-item flex cursor-pointer items-center overflow-hidden border-b border-b-gray-light px-5 py-4.5 hover:bg-gray-lighter xl:px-10",
                className,
            )}
            onClick={handleClick}
        >
            <div className="w-3/5 flex-shrink-0 space-y-1">
                <NoTranslate as="p" className="truncate text-lg font-bold leading-p2 tracking-tightest">
                    {coupon.name}
                </NoTranslate>
                <div className="flex items-center space-x-2 leading-p1 tracking-tightest text-gray-darker">
                    <span data-testid="coupon-list-coupon-type">{CouponTypeCopy[coupon.type]} Coupon</span>
                    <Dot color="secondary" data-testid="coupon-list-coupon-terms" />
                    <CouponSummary coupon={coupon} demographicData={demographicsData} />
                </div>
            </div>
            <div className="flex flex-auto justify-end" data-testid="coupon-list-coupon-uses">
                <Badge color="secondary" icon={<TicketIcon />} size="medium">
                    <span className="mr-2">
                        <Number maximumFractionDigits={0}>{coupon?.uses ?? 0}</Number>
                    </span>
                    Uses
                </Badge>
            </div>
            {isCouponEditable && (
                <div className="flex flex-auto justify-end space-x-2">
                    <SecondaryButton size="small" data-testid="coupon-list-edit-button" onClick={handleClick}>
                        <PenIcon />
                    </SecondaryButton>
                    <SecondaryButton size="small" data-testid="coupon-list-delete-button" onClick={handleDelete}>
                        <TrashIcon />
                    </SecondaryButton>
                </div>
            )}
            <PromptModal
                title="Delete Coupon?"
                isLoading={isLoading}
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onClose={() => setIsDeleteModalOpen(false)}
            >
                Are you sure you want to delete this coupon?
            </PromptModal>
        </div>
    ) : null;
};

const getValidSelectedTemplateIds = (
    options: PurchaseRuleShortcutDiscountOption | PurchaseRuleShortcutBXGYOption,
    demographicsData?: (DemographicsData | undefined)[],
): string[] => {
    if (!demographicsData || demographicsData.length === 0) {
        return options?.templateIds ?? [];
    }
    const selectedTemplateIds = options?.templateIds ?? [];
    const validSelectedTemplateIds = selectedTemplateIds.filter((id) =>
        demographicsData?.some((item) => item?.id === id),
    );
    return validSelectedTemplateIds;
};

function detectEquipmentCoupon(options: any, demographicData?: (DemographicsData | undefined)[]): boolean {
    return (
        options?.templateTypes?.includes(LineItemType.EQUIPMENT) ||
        (options?.templateIds?.length > 0 &&
            demographicData != null &&
            demographicData.length > 0 &&
            options.templateIds.every((id: string) => !demographicData.some((d) => d?.id === id)))
    );
}

function getDiscountSuffix(shortcut: any, options: any, demographicData?: (DemographicsData | undefined)[]): ReactNode {
    const isAbsolutePerUnit = [
        PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
        PurchaseRuleShortcutType.ABSOLUTE_DISCOUNT_PER_UNIT,
    ].includes(shortcut.type);

    let suffix: ReactNode = "per reservation";

    const isEquipmentCoupon = detectEquipmentCoupon(options, demographicData);

    if (isAbsolutePerUnit) {
        suffix = isEquipmentCoupon ? "per purchasable equipment" : "per person";
    } else if (shortcut.type === PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE) {
        suffix = "per reservation";
    }

    if (isMerchandiseCoupon(options)) {
        return "per merchandise";
    }

    if (isEquipmentCoupon) {
        return suffix;
    }

    let length = options.templateIds?.length ?? options.templateCodes?.length ?? 0;

    if (options.templateIds) {
        const validSelectedTemplateIds = getValidSelectedTemplateIds(options, demographicData);
        length = validSelectedTemplateIds.length;
    }

    if (length > 0) {
        const subTotalTemplateCodes = [
            PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC,
            PurchaseRuleShortcutTemplateCode.UNIT,
        ];

        let appliesToAllDemographics = false;
        let appliesToAddOns = false;

        if ("templateCodes" in options) {
            appliesToAllDemographics = subTotalTemplateCodes.some((code) => {
                return options.templateCodes?.includes(code);
            });
            appliesToAddOns = options.templateCodes?.includes(PurchaseRuleShortcutTemplateCode.UNIT) ?? false;
        }

        const count = appliesToAllDemographics || demographicData?.length === length ? "all" : length;
        const addonSuffix = appliesToAddOns ? " including add-ons" : "";

        suffix = (
            <>
                on {count === "all" ? count : <var data-var="count">{count}</var>} demographics{addonSuffix}
            </>
        );
    } else if ("templateTypes" in options) {
        // all demographics always, otherwise templateTypes are not set
        const appliesToAddOns = options.templateTypes?.includes(LineItemType.ADDON) ?? false;
        const addonSuffix = appliesToAddOns ? " including add-ons" : "";
        if (
            isAbsolutePerUnit ||
            appliesToAddOns ||
            (shortcut.options && "scope" in shortcut.options && shortcut.options.scope === CouponScope.PER_PERSON)
        ) {
            suffix = <>on all demographics{addonSuffix}</>;
        }
    }

    return suffix;
}

const isMerchandiseCoupon = (options: any): boolean => {
    if (!options) {
        return false;
    }

    if ("scope" in options && options.scope === CouponScope.PER_MERCHANDISE) {
        return true;
    }

    if ("templateTypes" in options && Array.isArray(options.templateTypes)) {
        return options.templateTypes.length === 1 && options.templateTypes[0] === LineItemType.WARE;
    }

    return false;
};

export const CouponSummary = ({ coupon, demographicData }: CouponSummaryProps) => {
    if (coupon.type === CouponType.UNLOCK) {
        return <span>Unlock</span>;
    }

    if (coupon.type === CouponType.VOUCHER) {
        return <VoucherCouponSummary coupon={coupon as VoucherCoupon} />;
    }

    if (coupon.type === CouponType.BXGY) {
        return <BxgyCouponSummary coupon={coupon as BxgyCoupon} />;
    }

    const shortcut = coupon.purchaseRule?.actions[0]?.shortcut;

    if (!shortcut) {
        return null;
    }

    const options = shortcut.options ?? {};
    const amount = Math.abs(shortcut?.amount);
    const suffix = getDiscountSuffix(shortcut, options, demographicData);

    return (
        <span>
            {shortcut.type === PurchaseRuleShortcutType.PERCENT ? (
                <var data-var="amount">{amount}%</var>
            ) : (
                <Currency>{amount}</Currency>
            )}{" "}
            {suffix}
        </span>
    );
};

const VoucherCouponSummary = ({ coupon }: { coupon: VoucherCoupon }) => {
    return (
        <span>
            <Currency>{coupon.amount}</Currency> {coupon.paymentMethod} voucher for{" "}
            <Currency>{coupon.purchaseAmount}</Currency>
        </span>
    );
};

const BxgyCouponSummary = ({ coupon }: { coupon: BxgyCoupon }) => {
    const seller = useSeller();
    const currency = getSymbol(seller.currency);
    const shortcut = coupon.purchaseRule?.actions[0].shortcut;
    if (!shortcut) {
        return null;
    }

    const options = shortcut.options as PurchaseRuleShortcutBXGYOption;
    const amountText =
        shortcut.type === PurchaseRuleShortcutType.BXGY_ABSOLUTE
            ? `${currency}${shortcut.amount}`
            : `${shortcut.amount}%`;
    return (
        <span>
            For every <var data-var="qualifyingQuantity">{options.qualifyingQuantity}</var> purchasable items booked get{" "}
            <var data-var="eligibleQuantity">{options.eligibleQuantity}</var> of equal or less value for{" "}
            <var data-var="amountText">{amountText}</var> off
        </span>
    );
};

export const CouponList = ({
    height,
    search,
    types,
    onResetClick,
}: {
    height: number;
    search: string;
    types: string[];
    onResetClick: () => void;
}) => {
    const fetchParams: any = { expand: "purchaseRule", source: Source.XOLA };
    if (search.length > 0) {
        fetchParams.search = search;
    }

    if (types.length > 0) {
        fetchParams["type[in]"] = types.join(",");
    }

    const {
        data: coupons,
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<Coupon>("coupons", fetchParams, {
        keepPreviousData: true,
        staleTime: 2000, // Only re-fetch if the change happens within 2s
    });

    const { data: catalogItems } = useCatalogItems();

    const demographicsData: (DemographicsData | undefined)[] = catalogItems
        .map((item) => {
            if (item.template?.id) {
                return {
                    id: item.template.id,
                    name: item.name,
                };
            }
        })
        .filter(Boolean);

    const { isGranted } = useAuthorization();
    const sortedCoupons = useMemo(() => sortBy(coupons, ["name"]), [coupons]);

    if (isLoading) {
        return (
            <>
                {range(1, 10).map((index) => (
                    <CouponItemSkeleton key={index} />
                ))}
            </>
        );
    }

    if (sortedCoupons.length === 0) {
        return (
            <div className="py-15 px-10">
                {isEmpty(search) ? (
                    <NoCouponsAlert />
                ) : (
                    <>
                        <NoResults>No results found</NoResults>
                        <div className="mt-10 flex justify-center">
                            <SecondaryButton onClick={onResetClick}>
                                <ArrowCcwIcon />
                                &nbsp;Clear Search
                            </SecondaryButton>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className={clsx(!isFetchingNextPage)}>
            <Virtuoso
                style={{ height }}
                data={sortedCoupons}
                endReached={async () => {
                    if (hasNextPage) {
                        fetchNextPage();
                    }
                }}
                itemContent={(index, coupon) => {
                    return (
                        <ErrorBoundary fallbackMessage={`Error rendering coupon ${coupon.id} ${coupon.name}`}>
                            <CouponItem
                                key={coupon.id}
                                coupon={coupon}
                                isCouponEditable={isGranted(Permission.MODIFY_COUPONS)}
                                demographicsData={demographicsData}
                            />
                        </ErrorBoundary>
                    );
                }}
            />
            {isFetchingNextPage ? <Loader /> : null}
        </div>
    );
};

export const NoCouponsAlert = () => {
    return (
        <div className="max-w-xl">
            <Alert color="primary">
                <div className="flex p-2">
                    <CouponIcon size="large" />

                    <div className="ml-2 flex-1" data-testid="coupon-list-no-coupons-alert">
                        <div className="mb-3 flex space-x-2">
                            <span className="text-md font-bold">No Coupons Found</span>
                        </div>

                        <span className="text-base">
                            You do not have any coupons yet. Please create your first coupon to get started.
                        </span>

                        <div className="mt-2 flex justify-end">
                            <CreateCouponButton size="small" />
                        </div>
                    </div>
                </div>
            </Alert>
        </div>
    );
};

const CouponItemSkeleton = () => {
    return (
        <Skeleton
            height={87}
            classNames={{
                container:
                    "!bg-white px-4 !w-full !justify-start border-0 border-b border-b-gray-lighter px-5 py-4.5 xl:px-10",
                text: "!w-full",
            }}
        >
            <div className="flex w-full items-center justify-between">
                <div className="w-3/5 flex-shrink-0 space-y-1">
                    <div className="space-x-2 text-lg leading-p2 tracking-tightest">
                        <SkeletonLine />
                    </div>

                    <div className="flex items-center space-x-3 leading-p1 tracking-tightest">
                        <span>Discount Coupon</span>
                        <span>50% on all demographics</span>
                    </div>
                </div>
                <div className="flex flex-auto justify-end">
                    <Badge color="secondary" icon={<TicketIcon />} size="medium" className="!text-gray">
                        <span className="mr-2">
                            <Number maximumFractionDigits={0}>{10}</Number>
                        </span>
                        Uses
                    </Badge>
                </div>
                <div className="flex flex-auto justify-end space-x-2">
                    <div className="flex items-center space-x-2">
                        <div className="inline-flex h-7.5 items-center rounded border border-gray-light px-3 py-2 text-base leading-base text-gray transition-colors">
                            <PenIcon />
                        </div>
                        <div className="inline-flex h-7.5 items-center rounded border border-gray-light px-3 py-2 text-base leading-base text-gray transition-colors">
                            <TrashIcon />
                        </div>
                    </div>
                </div>
            </div>
        </Skeleton>
    );
};
